import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  CircularProgress,
  InputAdornment,
  Typography,
  IconButton,
  Stack,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Person, Visibility, VisibilityOff, ArrowForward } from '@mui/icons-material';
import 'react-phone-number-input/style.css';
import { apiClientBff, ApiError } from '../../services';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  Button,
  Heading2,
  BodyText,
} from '../../design-system';
import { DESIGN_TOKENS } from '../../design-system/tokens';

type RegistrationSuccessMeta = {
  token?: string;
  password?: string;
};

interface RegistrationFormProps {
  isLoading: boolean;
  onSuccess: (message: string, email?: string, meta?: RegistrationSuccessMeta) => void;
  onError: (error: string) => void;
  onLoadingChange: (loading: boolean) => void;
  onSwitchToLogin?: () => void;
  hideLoginLink?: boolean;
  variant?: 'default' | 'webview';
}

interface FormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({ 
  isLoading, 
  onSuccess, 
  onError, 
  onLoadingChange,
  onSwitchToLogin,
  hideLoginLink = false,
  variant = 'default',
}) => {
  const { t } = useTranslation('forms');
  const [formData, setFormData] = useState<FormData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);

  const handleInputChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (field === 'email') {
      value = value.toLowerCase();
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Removed phone handling (not required by new API)

  // Helper to generate username from email
  const generateUsername = (email: string): string => {
    if (!email) return `user_${Math.floor(Math.random() * 10000)}`;
    const localPart = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${localPart}_${randomSuffix}`.substring(0, 30);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Username will be auto-generated if empty, so we don't validate existence here for user input
    // But if it exists (e.g. pre-filled), we validate format
    if (formData.username.trim()) {
       if (formData.username.length < 3 || formData.username.length > 50) {
        newErrors.username = t('validation.username_length');
      } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
        newErrors.username = t('validation.username_invalid');
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = t('validation.required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = t('validation.email_invalid');
      }
    }

    // Removed phone validation (not required by new API)

    if (!formData.password) {
      newErrors.password = t('validation.required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('validation.password_too_short');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwords_dont_match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!hasAcceptedTerms || !hasAcceptedPrivacy) {
      if (!hasAcceptedTerms) {
        setTermsError(t('register.terms_required', 'Você precisa aceitar os Termos de uso para continuar.'));
      }
      if (!hasAcceptedPrivacy) {
        setPrivacyError(t('register.privacy_required', 'Você precisa aceitar a Política de privacidade para continuar.'));
      }
      return;
    }
    
    onLoadingChange(true);

    try {
      // Auto-generate username if not present (field is hidden)
      // Ensure we have a valid username before sending
      let usernameToUse = formData.username.trim();
      
      if (!usernameToUse) {
        usernameToUse = generateUsername(formData.email);
        // Update state just in case, though we use the local variable for payload
        setFormData(prev => ({ ...prev, username: usernameToUse }));
      }

      // New registration payload to external API
      const payload = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        username: usernameToUse,
        roles: ["user"],
      };

      // Use BFF auth-specific POST (no /api/v2 prefix)
      const response = await apiClientBff.authPost<any>("/auth/register", payload);

      const extractJwtToken = (input: any): string | null => {
        const candidate =
          input?.token ??
          input?.accessToken ??
          input?.access_token ??
          input?.data?.token ??
          input?.data?.accessToken ??
          input?.data?.access_token;
        return typeof candidate === 'string' && candidate.trim().startsWith('eyJ') ? candidate.trim() : null;
      };

      // O novo endpoint idealmente retorna { message, user, token } com status 201.
      const isSuccess = Boolean(
        response?.token || response?.user || (typeof response?.message === 'string' && response.message.toLowerCase().includes('success'))
      );

      if (isSuccess) {
        let token = extractJwtToken(response);
        if (!token) {
          // Some register responses currently return token as an empty object.
          // Login immediately after successful registration to obtain the real JWT for onboarding flows.
          const loginResponse = await apiClientBff.authPost<any>('/auth/login', {
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
          });
          token = extractJwtToken(loginResponse);
          if (!token) {
            throw new Error('Conta criada, mas nao foi possivel autenticar automaticamente. Faça login para continuar.');
          }
        }
        const email = formData.email.trim().toLowerCase();
        onSuccess(t('register.success'), email, { token, password: formData.password });
      } else {
        const msg = response?.error || response?.message || t('register.error');
        throw new Error(msg);
      }
      
      // Reset form
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
      setErrors({});
      setHasAcceptedTerms(false);
      setTermsError(null);
      setHasAcceptedPrivacy(false);
      setPrivacyError(null);
      
    } catch (error: any) {
      console.error('❌ Erro ao criar conta:', error);
      const message = extractErrorMessage(error);
      onError(message);
    } finally {
      onLoadingChange(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {variant !== 'webview' && (
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Person sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.primary[700], mb: 2 }} />
          <Heading2 sx={{ mb: 1, color: DESIGN_TOKENS.colors.primary[700] }}>{t('register.title')}</Heading2>
          <BodyText sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
            {t('register.subtitle')}
          </BodyText>
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        <Stack spacing={variant === 'webview' ? 2.5 : 3}>
          {/* Removido Nome Completo (não exigido pela nova API) */}

          <TextField
            fullWidth
            label={t('register.email')}
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            required
          />

          {/* Removido Telefone (não exigido pela nova API) */}
          {/* Username field hidden/removed - auto-generated */}

          <TextField
            fullWidth
            label={t('register.password')}
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!errors.password}
            helperText={errors.password}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={t('register.confirm_password')}
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasAcceptedTerms}
                  onChange={(event) => {
                    setHasAcceptedTerms(event.target.checked);
                    if (termsError) setTermsError(null);
                  }}
                  sx={{ p: 0.75 }}
                />
              }
              sx={{
                alignItems: 'center',
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  mt: 0,
                },
              }}
              label={
                <Typography component="span" variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, lineHeight: 1.4 }}>
                  {t('register.terms_accept_prefix', 'Eu li e aceito os')}{' '}
                  <Link
                    component={RouterLink}
                    to="/terms"
                    variant="body2"
                    sx={{
                      color: DESIGN_TOKENS.colors.primary[700],
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {t('register.terms_link', 'Termos de uso')}
                  </Link>
                  .
                </Typography>
              }
            />
            {termsError && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0, ml: 4, color: DESIGN_TOKENS.colors.status.error[600] }}
              >
                {termsError}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={hasAcceptedPrivacy}
                  onChange={(event) => {
                    setHasAcceptedPrivacy(event.target.checked);
                    if (privacyError) setPrivacyError(null);
                  }}
                  sx={{ p: 0.75 }}
                />
              }
              sx={{
                alignItems: 'center',
                ml: 0,
                '& .MuiFormControlLabel-label': {
                  mt: 0,
                },
              }}
              label={
                <Typography component="span" variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, lineHeight: 1.4 }}>
                  {t('register.privacy_accept_prefix', 'Eu li e aceito a')}{' '}
                  <Link
                    component={RouterLink}
                    to="/privacy"
                    variant="body2"
                    sx={{
                      color: DESIGN_TOKENS.colors.primary[700],
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {t('register.privacy_link', 'Política de privacidade')}
                  </Link>
                  .
                </Typography>
              }
            />
            {privacyError && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0, ml: 4, color: DESIGN_TOKENS.colors.status.error[600] }}
              >
                {privacyError}
              </Typography>
            )}
          </Stack>

          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={isLoading || !hasAcceptedTerms || !hasAcceptedPrivacy}
            endIcon={!isLoading ? <ArrowForward /> : undefined}
          >
            {/* O loading visual principal é controlado pelo componente pai com fullScreen Loading.
                Aqui, apenas mantemos o texto do botão ou um indicador sutil se necessário, 
                mas para consistência com o pedido do usuário, deixamos o texto 'Criar Conta' 
                e o Loading fullScreen assume o destaque.
                Se isLoading for true, o botão fica desabilitado.
            */}
            {t('register.submit')}
          </Button>

          {/* Link para voltar ao login */}
          {!hideLoginLink && onSwitchToLogin && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {t('register.have_account')}{' '}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={onSwitchToLogin}
                  sx={{
                    color: DESIGN_TOKENS.colors.primary[700],
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {t('register.login_link')}
                </Link>
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
